import React, { useEffect, useMemo, useState } from "react";
import { Bot, User, ClipboardList, Copy, Check } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { cva } from "class-variance-authority";
import "katex/dist/katex.min.css";

const EVENT_FORM_SENTINEL = "__EVENT_FORM_REQUIRED__";
const GOALSET_FORM_SENTINEL = "__GOALSET_FORM_REQUIRED__";
const EVENT_FORM_PREFILL_START = "__EVENT_FORM_PREFILL__";
const EVENT_FORM_PREFILL_END = "__END_EVENT_FORM_PREFILL__";
const GOALSET_FORM_PREFILL_START = "__GOALSET_FORM_PREFILL__";
const GOALSET_FORM_PREFILL_END = "__END_GOALSET_FORM_PREFILL__";

const containerStyles = cva("mb-4 flex items-start gap-2", {
  variants: {
    role: {
      human: "justify-end",
      ai: "justify-start",    
    },
  },
  defaultVariants: {
    role: "ai",
  },
});

const bubbleStyles = cva("max-w-[80%] p-3 rounded-lg", {
  variants: {
    role: {
      human: "text-white",
      ai: "",
    },
  },
  defaultVariants: {
    role: "ai",
  },
});

interface ThemeColors {
  primary?: string;
  textPrimary?: string;
  userMessageBg?: string;
  aiMessageBg?: string;
  userMessageText?: string;
  [key: string]: unknown;
}

interface Message {
  id?: string;
  type?: string;
  role?: string;
  content?: string | { message?: string };
  file?: { name?: string; type?: string; url?: string };
  rows_w_missing_fields?: Array<{
    row_index?: number | string;
    id?: string;
    contact?: string;
    missing_fields?: string[];
  }>;
  thread_id?: string;
  [key: string]: unknown;
}

interface ResponseMessageProps {
  message: Message;
  content: string;
  theme: ThemeColors;
  user?: { name?: string; picture?: string };
  role: "human" | "ai";
}

type MissingFieldRow = {
  rowIndex: number;
  id: string;
  contact: string;
  missingFields: string[];
};

interface EventFormData {
  attendees: string;
  event_name: string;
  start_time: string;
  end_time: string;
  scheduling_instructions: string;
  color_id: string;
  color_id_desc: string;
  goal_name: string;
  goal_for_goal_desc: string;
  task_name: string;
  goal_for_task_desc: string;
  definition_of_done_for_task: string;
  description: string;
  location: string;
}

interface GoalSetFormData {
  event_id: string;
  event_summary: string;
  attendee_email: string;
  goal_name: string;
  task_name: string;
  color_id: string;
  id: string;
  desc: string;
  goal_for_goal: string;
  goal_for_task: string;
  definition_of_done: string;
  count: string;
}

const EVENT_FORM_FIELDS: { key: keyof EventFormData; label: string; placeholder: string; type?: string }[] = [
  { key: "event_name", label: "Event Name", placeholder: "e.g. Weekly team sync" },
  { key: "start_time", label: "Start Time (include date)", placeholder: "e.g. 2026-05-10 10:00 AM" },
  { key: "end_time", label: "End Time", placeholder: "e.g. 2026-05-10 11:00 AM" },
  { key: "attendees", label: "Attendees", placeholder: "Comma-separated emails" },
  {
    key: "scheduling_instructions",
    label: "Recurring Instructions",
    placeholder: "e.g. Repeat every Monday at 10:00 AM for 8 weeks",
  },
  { key: "color_id", label: "Color ID", placeholder: "e.g. 0, 1, 2" },
  {
    key: "color_id_desc",
    label: "Color ID Description",
    placeholder: "Describe what this color means (stored on Goals_book/{color_id}.desc)",
  },
  { key: "goal_name", label: "Goal Name", placeholder: "e.g. improve team communication" },
  { key: "goal_for_goal_desc", label: "Goal Description", placeholder: "Describe the goal in detail" },
  { key: "task_name", label: "Task Name", placeholder: "e.g. schedule weekly check-ins" },
  { key: "goal_for_task_desc", label: "Task Description", placeholder: "Describe what this task involves" },
  { key: "definition_of_done_for_task", label: "Definition of Done", placeholder: "How will you know this task is complete?" },
  { key: "description", label: "Event Description", placeholder: "Describe the event" },
  { key: "location", label: "Location", placeholder: "e.g. Office, Zoom, or address" },
];

const EMPTY_EVENT_FORM: EventFormData = {
  attendees: "",
  event_name: "",
  start_time: "",
  end_time: "",
  scheduling_instructions: "",
  color_id: "",
  color_id_desc: "",
  goal_name: "",
  goal_for_goal_desc: "",
  task_name: "",
  goal_for_task_desc: "",
  definition_of_done_for_task: "",
  description: "",
  location: "",
};

const EMPTY_GOALSET_FORM: GoalSetFormData = {
  event_id: "",
  event_summary: "",
  attendee_email: "",
  goal_name: "",
  task_name: "",
  color_id: "",
  id: "",
  desc: "",
  goal_for_goal: "",
  goal_for_task: "",
  definition_of_done: "",
  count: "",
};

const formatEventFormValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "email" in item) {
          return String((item as { email?: unknown }).email || "");
        }
        return String(item || "");
      })
      .filter(Boolean)
      .join(", ");
  }
  return String(value);
};

const eventFormFromProposedEvent = (proposedEvent: unknown): EventFormData => {
  if (!proposedEvent || typeof proposedEvent !== "object" || Array.isArray(proposedEvent)) {
    return EMPTY_EVENT_FORM;
  }

  const event = proposedEvent as Record<string, unknown>;
  return {
    attendees: formatEventFormValue(event.attendees),
    event_name: formatEventFormValue(event.event_name),
    start_time: formatEventFormValue(event.start_time),
    end_time: formatEventFormValue(event.end_time),
    scheduling_instructions: formatEventFormValue(event.scheduling_instructions),
    color_id: formatEventFormValue(event.color_id),
    color_id_desc: formatEventFormValue(event.color_id_desc),
    goal_name: formatEventFormValue(event.goal_name),
    goal_for_goal_desc: formatEventFormValue(event.goal_for_goal_desc),
    task_name: formatEventFormValue(event.task_name),
    goal_for_task_desc: formatEventFormValue(event.goal_for_task_desc),
    definition_of_done_for_task: formatEventFormValue(event.definition_of_done_for_task),
    description: formatEventFormValue(event.description),
    location: formatEventFormValue(event.location),
  };
};

const parseEventFormPrefill = (text: string): { content: string; form: EventFormData } => {
  const start = text.indexOf(EVENT_FORM_PREFILL_START);
  if (start === -1) {
    return { content: text, form: EMPTY_EVENT_FORM };
  }

  const encodedStart = start + EVENT_FORM_PREFILL_START.length;
  const end = text.indexOf(EVENT_FORM_PREFILL_END, encodedStart);
  if (end === -1) {
    return { content: text, form: EMPTY_EVENT_FORM };
  }

  const encoded = text.slice(encodedStart, end);
  const content = `${text.slice(0, start)}${text.slice(end + EVENT_FORM_PREFILL_END.length)}`.trim();
  try {
    return {
      content,
      form: eventFormFromProposedEvent(JSON.parse(decodeURIComponent(encoded))),
    };
  } catch {
    return { content, form: EMPTY_EVENT_FORM };
  }
};

const goalSetFormFromPrefill = (prefill: unknown): GoalSetFormData => {
  if (!prefill || typeof prefill !== "object" || Array.isArray(prefill)) {
    return EMPTY_GOALSET_FORM;
  }
  const obj = prefill as Record<string, unknown>;
  return {
    event_id: formatEventFormValue(obj.event_id),
    event_summary: formatEventFormValue(obj.event_summary),
    attendee_email: formatEventFormValue(obj.attendee_email),
    goal_name: formatEventFormValue(obj.goal_name),
    task_name: formatEventFormValue(obj.task_name),
    color_id: formatEventFormValue(obj.color_id),
    id: formatEventFormValue(obj.id),
    desc: formatEventFormValue(obj.desc),
    goal_for_goal: formatEventFormValue(obj.goal_for_goal),
    goal_for_task: formatEventFormValue(obj.goal_for_task),
    definition_of_done: formatEventFormValue(obj.definition_of_done),
    count: formatEventFormValue(obj.count),
  };
};

const parseGoalSetFormPrefill = (text: string): { content: string; form: GoalSetFormData } => {
  const start = text.indexOf(GOALSET_FORM_PREFILL_START);
  if (start === -1) {
    return { content: text, form: EMPTY_GOALSET_FORM };
  }
  const encodedStart = start + GOALSET_FORM_PREFILL_START.length;
  const end = text.indexOf(GOALSET_FORM_PREFILL_END, encodedStart);
  if (end === -1) {
    return { content: text, form: EMPTY_GOALSET_FORM };
  }
  const encoded = text.slice(encodedStart, end);
  const content = `${text.slice(0, start)}${text.slice(end + GOALSET_FORM_PREFILL_END.length)}`.trim();
  try {
    return {
      content,
      form: goalSetFormFromPrefill(JSON.parse(decodeURIComponent(encoded))),
    };
  } catch {
    return { content, form: EMPTY_GOALSET_FORM };
  }
};

const markdownStyles: Record<string, React.ComponentType<{ children?: React.ReactNode; href?: string; src?: string; alt?: string; inline?: boolean }>> = {
  h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-semibold mb-3">{children}</h2>,
  strong: ({ children }) => <strong className="font-bold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-500 underline"
    >
      {children}
    </a>
  ),
  img: ({ src, alt }) => <img src={src} alt={alt} className="mb-2" />,
  p: ({ children }) => <p className="mb-2">{children}</p>,
  code: ({ inline, children }) =>
    inline ? (
      <code className="px-1 py-0.5 bg-black/10 rounded">{children}</code>
    ) : (
      <pre className="p-2 bg-black/20 rounded text-sm overflow-x-auto">
        <code>{children}</code>
      </pre>
    ),
  ul: ({ children }) => <ul className="list-disc ml-6 mb-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ml-6 mb-2">{children}</ol>,
  li: ({ children }) => <li className="mb-1">{children}</li>,
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-black/10">{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-black/10">{children}</tr>,
  th: ({ children }) => (
    <th className="border border-black/20 px-3 py-2 text-left font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => <td className="border border-black/20 px-3 py-2">{children}</td>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-gray-400 pl-4 italic mb-2">
      {children}
    </blockquote>
  ),
};

const ResponseMessage = ({ message, content, theme, user, role }: ResponseMessageProps): React.ReactElement => {
  const isHuman = role === "human";
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">("idle");

  // --- CRM missing fields modal state ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeContactIndex, setActiveContactIndex] = useState(0);
  const [formByContact, setFormByContact] = useState<Record<string, Record<string, string>>>({});

  // --- Create Event form modal state ---
  const [isEventFormOpen, setIsEventFormOpen] = useState(false);
  const [eventForm, setEventForm] = useState<EventFormData>(EMPTY_EVENT_FORM);
  const [isGoalSetFormOpen, setIsGoalSetFormOpen] = useState(false);
  const [goalSetForm, setGoalSetForm] = useState<GoalSetFormData>(EMPTY_GOALSET_FORM);

  const missingFieldRows = useMemo<MissingFieldRow[]>(() => {
    const fromPayload = Array.isArray(message.rows_w_missing_fields)
      ? message.rows_w_missing_fields
      : [];
    if (!fromPayload.length) {
      return [];
    }
    return fromPayload.map((row, index) => ({
      rowIndex: Number(row?.row_index ?? index + 1),
      id: String(row?.id ?? ""),
      contact: String(row?.contact ?? "").trim() || `Contact ${index + 1}`,
      missingFields: Array.isArray(row?.missing_fields)
        ? row.missing_fields.map((x) => String(x).trim()).filter(Boolean)
        : [],
    }));
  }, [message.rows_w_missing_fields]);

  const isMissingFieldsMessage =
    !isHuman &&
    missingFieldRows.length > 0 &&
    missingFieldRows.some((row) => row.missingFields.length > 0);

  // Detect the create-event form sentinel
  const isEventFormRequired =
    !isHuman &&
    typeof content === "string" &&
    content.includes(EVENT_FORM_SENTINEL);
  const isGoalSetFormRequired =
    !isHuman &&
    typeof content === "string" &&
    content.includes(GOALSET_FORM_SENTINEL);

  const eventFormPrefill = useMemo(
    () => parseEventFormPrefill(String(content || "")),
    [content],
  );
  const goalSetFormPrefill = useMemo(
    () => parseGoalSetFormPrefill(eventFormPrefill.content),
    [eventFormPrefill.content],
  );

  const cleanContent = (
    isEventFormRequired
      ? goalSetFormPrefill.content.replace(EVENT_FORM_SENTINEL, "")
      : goalSetFormPrefill.content
  )
    .replace(GOALSET_FORM_SENTINEL, "")
    .trim();

  const messageCopyText = useMemo(() => {
    if (cleanContent) {
      return cleanContent;
    }

    if (isMissingFieldsMessage && missingFieldRows.length) {
      const lines = missingFieldRows.map(
        (row) => `- ${row.contact}: ${row.missingFields.join(", ")}`,
      );
      return ["Contacts Missing Required Fields", ...lines].join("\n");
    }

    if (message.file?.name) {
      return String(message.file.name);
    }

    if (typeof content === "string" && content.trim()) {
      return content.trim();
    }

    return "";
  }, [cleanContent, content, isMissingFieldsMessage, message.file?.name, missingFieldRows]);

  const activeRow = missingFieldRows[activeContactIndex];
  const activeKey = `${activeRow?.id || activeRow?.contact || activeContactIndex}`;
  const activeValues = formByContact[activeKey] || {};

  const updateFieldValue = (fieldName: string, value: string) => {
    setFormByContact((prev) => ({
      ...prev,
      [activeKey]: {
        ...(prev[activeKey] || {}),
        [fieldName]: value,
      },
    }));
  };

  const handleEventFormSubmit = () => {
    window.postMessage(
      {
        type: "SUBMIT_CREATE_EVENT_FORM",
        thread_id: typeof message.thread_id === "string" ? message.thread_id : undefined,
        formData: {
          ...eventForm,
        },
      },
      window.location.origin,
    );
    setEventForm(EMPTY_EVENT_FORM);
  };

  const openEventFormInNewWindow = (initialForm: EventFormData) => {
    const popup = window.open(
      "about:blank",
      "_blank",
      "width=760,height=900",
    );
    if (!popup) {
      // Force explicit user action if browser blocks popups.
      window.alert("Popup blocked. Please allow popups for this site and click 'Fill in event details' again.");
      return;
    }

    const escapeHtml = (value: string): string =>
      value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");

    const textareaKeys = new Set([
      "definition_of_done_for_task",
      "goal_for_goal_desc",
      "goal_for_task_desc",
      "scheduling_instructions",
      "description",
    ]);

    const fieldMarkup = EVENT_FORM_FIELDS.map(({ key, label, placeholder, type }) => {
      const safeKey = escapeHtml(String(key));
      const safeLabel = escapeHtml(label);
      const safePlaceholder = escapeHtml(placeholder);
      const safeValue = escapeHtml(initialForm[key] || "");
      if (textareaKeys.has(String(key))) {
        return `
          <label class="field">
            <span>${safeLabel}</span>
            <textarea name="${safeKey}" rows="3" placeholder="${safePlaceholder}">${safeValue}</textarea>
          </label>
        `;
      }
      return `
        <label class="field">
          <span>${safeLabel}</span>
          <input type="${escapeHtml(type || "text")}" name="${safeKey}" value="${safeValue}" placeholder="${safePlaceholder}" />
        </label>
      `;
    }).join("");

    const threadId = JSON.stringify(
      typeof message.thread_id === "string" ? message.thread_id : "",
    );
    const origin = JSON.stringify(window.location.origin);

    popup.document.open();
    popup.document.write(`<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Fill in event details</title>
    <style>
      body { margin: 0; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif; background: #f6f7fb; color: #111827; }
      .container { max-width: 720px; margin: 0 auto; padding: 20px; }
      .card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.08); overflow: hidden; }
      .header { padding: 16px 20px; border-bottom: 1px solid #e5e7eb; font-size: 16px; font-weight: 600; }
      .body { padding: 16px 20px; display: grid; gap: 14px; max-height: calc(100vh - 180px); overflow: auto; }
      .field { display: grid; gap: 6px; }
      .field > span { font-size: 13px; color: #374151; font-weight: 500; }
      input, textarea { width: 100%; box-sizing: border-box; border: 1px solid #d1d5db; border-radius: 8px; padding: 10px 12px; font-size: 14px; background: #fff; color: #111827; }
      textarea { resize: vertical; min-height: 74px; }
      .footer { padding: 14px 20px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 10px; }
      button { border: 0; border-radius: 8px; padding: 10px 14px; font-size: 14px; font-weight: 600; cursor: pointer; }
      .cancel { background: #f3f4f6; color: #111827; }
      .submit { background: ${String(theme.primary || "#7c4dff")}; color: #fff; }
    </style>
  </head>
  <body>
    <div class="container">
      <form id="event-form" class="card">
        <div class="header">Event Details</div>
        <div class="body">
          ${fieldMarkup}
        </div>
        <div class="footer">
          <button type="button" class="cancel" id="cancel-btn">Cancel</button>
          <button type="submit" class="submit">Submit</button>
        </div>
      </form>
    </div>
    <script>
      const threadId = ${threadId};
      const origin = ${origin};
      const form = document.getElementById("event-form");
      const cancel = document.getElementById("cancel-btn");
      cancel.addEventListener("click", () => window.close());
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        const fd = new FormData(form);
        const formData = {};
        fd.forEach((value, key) => {
          formData[key] = String(value || "");
        });
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            {
              type: "SUBMIT_CREATE_EVENT_FORM",
              thread_id: threadId || undefined,
              formData,
            },
            origin,
          );
        }
        window.close();
      });
    </script>
  </body>
</html>`);
    popup.document.close();
    popup.focus();
  };

  const openGoalSetFormInNewWindow = (initialForm: GoalSetFormData) => {
    const popup = window.open(
      "about:blank",
      "_blank",
      "width=760,height=900",
    );
    if (!popup) {
      window.alert("Popup blocked. Please allow popups for this site and click 'Fill in goal details' again.");
      return;
    }

    const escapeHtml = (value: string): string =>
      value
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");

    const fields: Array<{ key: keyof GoalSetFormData; label: string; placeholder: string; type?: string; readOnly?: boolean; textarea?: boolean }> = [
      { key: "event_summary", label: "Event Summary", placeholder: "", readOnly: true },
      { key: "goal_name", label: "Goal Name", placeholder: "e.g. health" },
      { key: "task_name", label: "Task Name", placeholder: "e.g. morning_walk" },
      { key: "color_id", label: "Color ID", placeholder: "e.g. 0" },
      { key: "id", label: "ID", placeholder: "e.g. 0" },
      { key: "desc", label: "Description", placeholder: "Color description", textarea: true },
      { key: "goal_for_goal", label: "Goal for Goal", placeholder: "High-level goal description", textarea: true },
      { key: "goal_for_task", label: "Goal for Task", placeholder: "Task-specific goal description", textarea: true },
      { key: "definition_of_done", label: "Definition of Done", placeholder: "Completion criteria", textarea: true },
      { key: "count", label: "Count", placeholder: "e.g. 1", type: "number" },
      { key: "event_id", label: "Event ID", placeholder: "" },
      { key: "attendee_email", label: "Attendee Email", placeholder: "name@example.com" },
    ];

    const fieldMarkup = fields
      .map(({ key, label, placeholder, type, readOnly, textarea }) => {
        const safeKey = escapeHtml(String(key));
        const safeLabel = escapeHtml(label);
        const safePlaceholder = escapeHtml(placeholder);
        const safeValue = escapeHtml(initialForm[key] || "");
        if (textarea) {
          return `
            <label class="field">
              <span>${safeLabel}</span>
              <textarea name="${safeKey}" rows="3" placeholder="${safePlaceholder}">${safeValue}</textarea>
            </label>
          `;
        }
        return `
          <label class="field">
            <span>${safeLabel}</span>
            <input
              type="${escapeHtml(type || "text")}"
              name="${safeKey}"
              value="${safeValue}"
              placeholder="${safePlaceholder}"
              ${readOnly ? "readonly" : ""}
            />
          </label>
        `;
      })
      .join("");

    const threadId = JSON.stringify(
      typeof message.thread_id === "string" ? message.thread_id : "",
    );
    const origin = JSON.stringify(window.location.origin);

    popup.document.open();
    popup.document.write(`<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>Fill in goal details</title>
    <style>
      body { margin: 0; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif; background: #f6f7fb; color: #111827; }
      .container { max-width: 720px; margin: 0 auto; padding: 20px; }
      .card { background: #ffffff; border: 1px solid #e5e7eb; border-radius: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.08); overflow: hidden; }
      .header { padding: 16px 20px; border-bottom: 1px solid #e5e7eb; font-size: 16px; font-weight: 600; }
      .body { padding: 16px 20px; display: grid; gap: 14px; max-height: calc(100vh - 180px); overflow: auto; }
      .field { display: grid; gap: 6px; }
      .field > span { font-size: 13px; color: #374151; font-weight: 500; }
      input, textarea { width: 100%; box-sizing: border-box; border: 1px solid #d1d5db; border-radius: 8px; padding: 10px 12px; font-size: 14px; background: #fff; color: #111827; }
      input[readonly] { background: #f3f4f6; color: #6b7280; }
      textarea { resize: vertical; min-height: 74px; }
      .footer { padding: 14px 20px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 10px; }
      button { border: 0; border-radius: 8px; padding: 10px 14px; font-size: 14px; font-weight: 600; cursor: pointer; }
      .cancel { background: #f3f4f6; color: #111827; }
      .submit { background: ${String(theme.primary || "#7c4dff")}; color: #fff; }
    </style>
  </head>
  <body>
    <div class="container">
      <form id="goalset-form" class="card">
        <div class="header">Goal Set Details</div>
        <div class="body">
          ${fieldMarkup}
        </div>
        <div class="footer">
          <button type="button" class="cancel" id="cancel-btn">Cancel</button>
          <button type="submit" class="submit">Submit</button>
        </div>
      </form>
    </div>
    <script>
      const threadId = ${threadId};
      const origin = ${origin};
      const form = document.getElementById("goalset-form");
      const cancel = document.getElementById("cancel-btn");
      cancel.addEventListener("click", () => window.close());
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        const fd = new FormData(form);
        const formData = {};
        fd.forEach((value, key) => {
          formData[key] = String(value || "");
        });
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage(
            {
              type: "SUBMIT_GOALSET_FORM",
              thread_id: threadId || undefined,
              formData,
            },
            origin,
          );
        }
        window.close();
      });
    </script>
  </body>
</html>`);
    popup.document.close();
    popup.focus();
  };

  useEffect(() => {
    if (!isEventFormOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsEventFormOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isEventFormOpen]);

  useEffect(() => {
    if (!isGoalSetFormOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsGoalSetFormOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isGoalSetFormOpen]);

  const handleGoalSetFormSubmit = () => {
    window.postMessage(
      {
        type: "SUBMIT_GOALSET_FORM",
        thread_id: typeof message.thread_id === "string" ? message.thread_id : undefined,
        formData: { ...goalSetForm },
      },
      window.location.origin,
    );
    setIsGoalSetFormOpen(false);
    setGoalSetForm(EMPTY_GOALSET_FORM);
  };

  const handleCopyMessage = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    event.stopPropagation();
    if (!messageCopyText.trim()) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(messageCopyText);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = messageCopyText;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopyState("copied");
      window.setTimeout(() => setCopyState("idle"), 1600);
    } catch {
      setCopyState("error");
      window.setTimeout(() => setCopyState("idle"), 2000);
    }
  };

  return (
    <div className={containerStyles({ role })}>
      {!isHuman && (
        <div
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-1"
          style={{ backgroundColor: theme.primary }}
        >
          <Bot size={16} color="white" />
        </div>
      )}

      <div
        className={bubbleStyles({ role })}
        style={{
          backgroundColor: isHuman ? theme.userMessageBg : theme.aiMessageBg,
          color: isHuman ? theme.userMessageText : theme.textPrimary,
        }}
      >
        {message.file && (
          <div className="mb-2">
            {message.file.type?.startsWith("image/") ? (
              <img
                src={message.file.url}
                alt={message.file.name}
                className="max-w-full h-48 object-contain rounded"
              />
            ) : (
              <p className="text-sm">{message.file.name}</p>
            )}
          </div>
        )}

        {isMissingFieldsMessage ? (
          <div>
            <h4 className="font-semibold mb-2">Contacts Missing Required Fields</h4>
            <div className="space-y-2">
              {missingFieldRows.map((row, index) => (
                <div key={`${row.contact}-${index}`} className="rounded-md border border-black/10 p-2">
                  <div className="font-medium">{row.contact}</div>
                  <div className="text-sm opacity-90">
                    {row.missingFields.join(", ")}
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-3 px-3 py-2 rounded-md text-sm font-medium"
              style={{
                backgroundColor: String(theme.primary || "#7c4dff"),
                color: "#fff",
              }}
              onClick={() => {
                setActiveContactIndex(0);
                setIsModalOpen(true);
              }}
            >
              Update customer profiles
            </button>
          </div>
        ) : isEventFormRequired ? (
          <div>
            {cleanContent && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={markdownStyles as any}
              >
                {cleanContent}
              </ReactMarkdown>
            )}
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-2"
              style={{ color: String(theme.primary || "#7c4dff") }}
              onClick={() => {
                openEventFormInNewWindow(eventFormPrefill.form);
              }}
            >
              <ClipboardList size={14} />
              Fill in event details
            </button>
            {isGoalSetFormRequired && (
              <button
                type="button"
                className="ml-3 mt-2 inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-2"
                style={{ color: String(theme.primary || "#7c4dff") }}
                onClick={() => {
                  openGoalSetFormInNewWindow(goalSetFormPrefill.form);
                }}
              >
                <ClipboardList size={14} />
                Fill in goal details
              </button>
            )}
          </div>
        ) : isGoalSetFormRequired ? (
          <div>
            {cleanContent && (
              <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkMath]}
                rehypePlugins={[rehypeKatex]}
                components={markdownStyles as any}
              >
                {cleanContent}
              </ReactMarkdown>
            )}
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-2"
              style={{ color: String(theme.primary || "#7c4dff") }}
              onClick={() => {
                openGoalSetFormInNewWindow(goalSetFormPrefill.form);
              }}
            >
              <ClipboardList size={14} />
              Fill in goal details
            </button>
          </div>
        ) : (
          cleanContent && (
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeKatex]}
              components={markdownStyles as any}
            >
              {cleanContent}
            </ReactMarkdown>
          )
        )}

        <div className="mt-2 flex justify-end">
          <button
            type="button"
            onClick={handleCopyMessage}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] opacity-70 transition-opacity hover:opacity-100"
            style={{
              color: isHuman ? theme.userMessageText : theme.textPrimary,
              backgroundColor: isHuman ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.06)",
            }}
            aria-label="Copy message"
            title={copyState === "copied" ? "Copied" : "Copy message"}
          >
            {copyState === "copied" ? <Check size={12} /> : <Copy size={12} />}
            {copyState === "copied" ? "Copied" : copyState === "error" ? "Failed" : "Copy"}
          </button>
        </div>
      </div>

      {isHuman && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full mt-1 overflow-hidden">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name || "User"}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = "none";
                const next = target.nextSibling as HTMLElement;
                if (next) next.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className="w-full h-full rounded-full flex items-center justify-center"
            style={{
              backgroundColor: theme.userMessageBg,
              display: user?.picture ? "none" : "flex",
            }}
          >
            <User size={16} color="white" />
          </div>
        </div>
      )}

      {/* CRM contacts missing-fields modal */}
      {isMissingFieldsMessage && isModalOpen && activeRow && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            className="w-full max-w-2xl rounded-lg p-4"
            style={{ backgroundColor: String(theme.aiMessageBg || "#fff"), color: String(theme.textPrimary || "#111") }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Update Customer Profile</h3>
              <button type="button" onClick={() => setIsModalOpen(false)}>
                Close
              </button>
            </div>

            <div className="mb-2 text-sm opacity-80">
              Contact {activeContactIndex + 1} of {missingFieldRows.length}
            </div>
            <div className="mb-4 font-medium">{activeRow.contact}</div>

            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
              {activeRow.missingFields.map((fieldName) => (
                <label key={fieldName} className="block">
                  <div className="mb-1 text-sm font-medium">{fieldName}</div>
                  <input
                    type="text"
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    value={activeValues[fieldName] || ""}
                    onChange={(e) => updateFieldValue(fieldName, e.target.value)}
                    placeholder={`Enter ${fieldName}`}
                  />
                </label>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <button
                type="button"
                className="px-3 py-2 rounded-md border"
                disabled={activeContactIndex === 0}
                onClick={() => setActiveContactIndex((idx) => Math.max(0, idx - 1))}
              >
                Back
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded-md text-white"
                style={{ backgroundColor: String(theme.primary || "#7c4dff") }}
                onClick={() => {
                  if (activeContactIndex < missingFieldRows.length - 1) {
                    setActiveContactIndex((idx) => idx + 1);
                    return;
                  }
                  setIsModalOpen(false);
                }}
              >
                {activeContactIndex < missingFieldRows.length - 1 ? "Next" : "Finish"}
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded-md text-white"
                style={{ backgroundColor: "#059669" }}
                onClick={() => {
                  const updatedProfiles = missingFieldRows.map((row, idx) => {
                    const key = `${row.id || row.contact || idx}`;
                    return {
                      row_index: row.rowIndex,
                      id: row.id || null,
                      contact: row.contact,
                      updated_fields: formByContact[key] || {},
                    };
                  });
                  window.postMessage(
                    {
                      type: "SUBMIT_CUSTOMER_PROFILE_UPDATES",
                      thread_id:
                        typeof message.thread_id === "string"
                          ? message.thread_id
                          : undefined,
                      updated_profiles: updatedProfiles,
                    },
                    window.location.origin,
                  );
                  setIsModalOpen(false);
                }}
              >
                Submit updates
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Event missing-fields form modal */}
      {isEventFormRequired && isEventFormOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => {}}
        >
          <div
            className="w-full max-w-xl rounded-xl shadow-2xl"
            style={{
              backgroundColor: String(theme.aiMessageBg || "#fff"),
              color: String(theme.textPrimary || "#111"),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: String(theme.primary || "#7c4dff") }}
                >
                  <ClipboardList size={14} color="#fff" />
                </div>
                <h3 className="text-base font-semibold">Event Details</h3>
              </div>
              <button
                type="button"
                className="text-sm opacity-60 hover:opacity-100 transition-opacity"
                onClick={() => setIsEventFormOpen(false)}
              >
                ✕
              </button>
            </div>

            {/* Form body */}
            <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
              {EVENT_FORM_FIELDS.map(({ key, label, placeholder, type }) => (
                <div key={key}>
                  <label className="block text-sm font-medium mb-1 opacity-80">
                    {label}
                  </label>
                  {key === "definition_of_done_for_task" || key === "goal_for_goal_desc" || key === "goal_for_task_desc" || key === "scheduling_instructions" || key === "description" ? (
                    <textarea
                      rows={2}
                      className="w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2"
                      style={{
                        borderColor: "rgba(0,0,0,0.15)",
                        backgroundColor: "rgba(0,0,0,0.03)",
                        // @ts-ignore
                        "--tw-ring-color": String(theme.primary || "#7c4dff"),
                      }}
                      placeholder={placeholder}
                      value={eventForm[key]}
                      onChange={(e) =>
                        setEventForm((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    />
                  ) : (
                    <input
                      type={type || "text"}
                      className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                      style={{
                        borderColor: "rgba(0,0,0,0.15)",
                        backgroundColor: "rgba(0,0,0,0.03)",
                        // @ts-ignore
                        "--tw-ring-color": String(theme.primary || "#7c4dff"),
                      }}
                      placeholder={placeholder}
                      value={eventForm[key]}
                      onChange={(e) =>
                        setEventForm((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div
              className="flex items-center justify-end gap-3 px-5 py-4 border-t"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
            >
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: String(theme.primary || "#7c4dff") }}
                onClick={handleEventFormSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Goal Set form modal */}
      {isGoalSetFormRequired && isGoalSetFormOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => {}}
        >
          <div
            className="w-full max-w-xl rounded-xl shadow-2xl"
            style={{
              backgroundColor: String(theme.aiMessageBg || "#fff"),
              color: String(theme.textPrimary || "#111"),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: String(theme.primary || "#7c4dff") }}
                >
                  <ClipboardList size={14} color="#fff" />
                </div>
                <h3 className="text-base font-semibold">Goal Set Details</h3>
              </div>
              <button
                type="button"
                className="text-sm opacity-60 hover:opacity-100 transition-opacity"
                onClick={() => setIsGoalSetFormOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">Event Summary</label>
                <input
                  type="text"
                  className="w-full rounded-lg border px-3 py-2 text-sm bg-black/5"
                  style={{ borderColor: "rgba(0,0,0,0.15)" }}
                  value={goalSetForm.event_summary}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">Goal Name</label>
                <input
                  type="text"
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    // @ts-ignore
                    "--tw-ring-color": String(theme.primary || "#7c4dff"),
                  }}
                  placeholder="e.g. health"
                  value={goalSetForm.goal_name}
                  onChange={(e) => setGoalSetForm((prev) => ({ ...prev, goal_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">Task Name</label>
                <input
                  type="text"
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    // @ts-ignore
                    "--tw-ring-color": String(theme.primary || "#7c4dff"),
                  }}
                  placeholder="e.g. morning_walk"
                  value={goalSetForm.task_name}
                  onChange={(e) => setGoalSetForm((prev) => ({ ...prev, task_name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">Color ID</label>
                <input
                  type="text"
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    // @ts-ignore
                    "--tw-ring-color": String(theme.primary || "#7c4dff"),
                  }}
                  placeholder="e.g. 0"
                  value={goalSetForm.color_id}
                  onChange={(e) => setGoalSetForm((prev) => ({ ...prev, color_id: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">ID</label>
                <input
                  type="text"
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    // @ts-ignore
                    "--tw-ring-color": String(theme.primary || "#7c4dff"),
                  }}
                  placeholder="e.g. 0"
                  value={goalSetForm.id}
                  onChange={(e) => setGoalSetForm((prev) => ({ ...prev, id: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">Description</label>
                <textarea
                  rows={2}
                  className="w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    // @ts-ignore
                    "--tw-ring-color": String(theme.primary || "#7c4dff"),
                  }}
                  placeholder="Color description"
                  value={goalSetForm.desc}
                  onChange={(e) => setGoalSetForm((prev) => ({ ...prev, desc: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">Goal for Goal</label>
                <textarea
                  rows={2}
                  className="w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    // @ts-ignore
                    "--tw-ring-color": String(theme.primary || "#7c4dff"),
                  }}
                  placeholder="High-level goal description"
                  value={goalSetForm.goal_for_goal}
                  onChange={(e) => setGoalSetForm((prev) => ({ ...prev, goal_for_goal: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">Goal for Task</label>
                <textarea
                  rows={2}
                  className="w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    // @ts-ignore
                    "--tw-ring-color": String(theme.primary || "#7c4dff"),
                  }}
                  placeholder="Task-specific goal description"
                  value={goalSetForm.goal_for_task}
                  onChange={(e) => setGoalSetForm((prev) => ({ ...prev, goal_for_task: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">Definition of Done</label>
                <textarea
                  rows={2}
                  className="w-full rounded-lg border px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    // @ts-ignore
                    "--tw-ring-color": String(theme.primary || "#7c4dff"),
                  }}
                  placeholder="Completion criteria"
                  value={goalSetForm.definition_of_done}
                  onChange={(e) => setGoalSetForm((prev) => ({ ...prev, definition_of_done: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 opacity-80">Count</label>
                <input
                  type="number"
                  min={1}
                  className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{
                    borderColor: "rgba(0,0,0,0.15)",
                    backgroundColor: "rgba(0,0,0,0.03)",
                    // @ts-ignore
                    "--tw-ring-color": String(theme.primary || "#7c4dff"),
                  }}
                  placeholder="e.g. 1"
                  value={goalSetForm.count}
                  onChange={(e) => setGoalSetForm((prev) => ({ ...prev, count: e.target.value }))}
                />
              </div>
            </div>

            <div
              className="flex items-center justify-end gap-3 px-5 py-4 border-t"
              style={{ borderColor: "rgba(0,0,0,0.1)" }}
            >
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: String(theme.primary || "#7c4dff") }}
                onClick={handleGoalSetFormSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponseMessage;
